import _ from "lodash";
import moment from "moment";

import { sumFloatingPointNumbers, sumNumbers } from "utils";

import {
  AggregatedCropFootprint,
  BasicFieldDetails,
  CalculatedCropFootprintResponse,
  CalculatedFarmFootprintResponse,
  FOOTPRINTING_BANNER_KEYS,
  FarmDetailsResponse,
  FootprintCalculationSummary,
  FootprintSummaryMetrics,
  FootprintingFootprintAggregation,
  FootprintingResultsDataTable,
  FootprintingResultsSubDataRowData,
  FootprintingResultsTableRowData,
  PracticeCrop,
  SubDataRowSource,
  WithInjectedName,
} from "models";

export function bannerKey(farmId: string, year: number, bannerType: (typeof FOOTPRINTING_BANNER_KEYS)[keyof typeof FOOTPRINTING_BANNER_KEYS]) {
  return `${year}-${farmId}-${bannerType}-complete-banner-shown`;
}

/**
 * Safely extract emission values with null checks
 * @param footprint - The footprint aggregation object
 * @param type - The type of value to extract (avg, min, max)
 * @returns The emission value or 0 if not available
 */
const extractEmissionValue = (footprint: FootprintingFootprintAggregation | undefined, type: "avg" | "min" | "max" = "avg"): number =>
  footprint === undefined ? 0 : (footprint.amount.emission[type] ?? 0) + (footprint.penalty.emission[type] ?? 0);

const createDataRow = (sourceEmissions: number, cropHas: number, cropYield: number, totalEmissions: number): FootprintingResultsTableRowData => ({
  totalCO2e: sourceEmissions,
  perHaCO2e: sourceEmissions / cropHas,
  perKgYieldCO2e: sourceEmissions / cropYield,
  shareOfTotal: _.round(sourceEmissions / totalEmissions, 4),
});

const createSubdataRow = (
  source: SubDataRowSource,
  sourceEmissions: number,
  cropHa: number,
  cropYield: number,
  totalEmissions: number
): FootprintingResultsSubDataRowData => ({
  source,
  ...createDataRow(sourceEmissions, cropHa, cropYield, totalEmissions),
});

const EMPTY_SOURCE_ROW = { data: createDataRow(0, 1, 1, 1), subData: [] };
const EMPTY_SUMMARY: FootprintCalculationSummary = {
  overall: { totalCO2e: 0, perKgYieldCO2e: 0, perHaCO2e: 0 },
  tableData: {
    fertilizer: { ...EMPTY_SOURCE_ROW },
    plantProtection: { ...EMPTY_SOURCE_ROW },
    transportation: { ...EMPTY_SOURCE_ROW },
    fieldWork: { ...EMPTY_SOURCE_ROW },
    energy: { ...EMPTY_SOURCE_ROW },
    waste: { ...EMPTY_SOURCE_ROW },
    refrigerants: { ...EMPTY_SOURCE_ROW },
    residue: { ...EMPTY_SOURCE_ROW },
    luc: { ...EMPTY_SOURCE_ROW },
    other: { ...EMPTY_SOURCE_ROW },
  },
};

/**
 * Comprehensive data processor for footprint results page
 * Transforms raw farm and crop footprint data into structured format for all UI components
 * @param farmFootprintData - The calculated farm footprint data from the API
 * @param cropFootprintData - Array of calculated crop footprint data
 * @param farmSize - Total farm size in hectares (for per-hectare calculations)
 * @param totalYield - Total crop yield in kg (for per-yield calculations)
 * @param t - Translation function
 * @returns Structured data for all UI components (summary cards, pie chart, table)
 */
export function createFootprintCalculationSummary(
  farm: FarmDetailsResponse,
  fields: BasicFieldDetails[] | undefined,
  farmCalculation: CalculatedFarmFootprintResponse | undefined,
  cropCalculation: CalculatedCropFootprintResponse | undefined,
  cropFootprint: AggregatedCropFootprint | undefined
): FootprintCalculationSummary | null {
  if (!farmCalculation || !cropCalculation || !cropFootprint || !fields || fields.length === 0) {
    return _.cloneDeep(EMPTY_SUMMARY);
  }

  const farmSize = farm.size ?? 1;
  const totalCropHectars = sumNumbers(fields.map((f) => f.size ?? 0));
  const totalCropYieldKg = 1000 * (cropFootprint.totalWeightTonsPerHa ?? 0) * totalCropHectars;

  if (totalCropHectars < 0.001) {
    return null;
  }

  const hectarRatio = totalCropHectars / farmSize;

  /** FARM ENERGYs */
  const farmDieselEmissions = extractEmissionValue(farmCalculation.calculatedFootprint.energy.dieselFootprint) * hectarRatio;
  const farmBenzinEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.benzinFootprint) * hectarRatio;
  const farmNaturalGasEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.naturalGasFootprint) * hectarRatio;
  const farmHeatingOilEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.heatingOilFootprint) * hectarRatio;
  const farmDirtyElectricityEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.electricityFootprint) * hectarRatio;
  const farmRenewableElectricityEmission = extractEmissionValue(farmCalculation.calculatedFootprint.energy.renewableElectricityFootprint) * hectarRatio;
  const farmElectricityEmission = sumNumbers([farmDirtyElectricityEmission, farmRenewableElectricityEmission]);
  const totalFarmEnergyEmission = sumNumbers([
    farmDieselEmissions,
    farmBenzinEmission,
    farmNaturalGasEmission,
    farmHeatingOilEmission,
    farmElectricityEmission,
  ]);

  /** FARM WATER */
  const farmWastewaterEmission = extractEmissionValue(farmCalculation.calculatedFootprint.water.wastewaterFootprint) * hectarRatio;

  /** FARM WASTE */
  const totalWasteEmission = extractEmissionValue(farmCalculation.calculatedFootprint.misc.wasteFootprint) * hectarRatio;

  /** FARM REFRIGERANTS */
  const totalRefrigerantUsageEmissions = farmCalculation.calculatedFootprint.misc.refrigerantFootprint.reduce(
    (sum, refrigerant) => sum + extractEmissionValue(refrigerant.emissions) * hectarRatio,
    0
  );

  /** LAND USE CHANGE */
  const totalLandUseChangeEmission = farmCalculation.calculatedFootprint.misc.landUseChangeFooprint.reduce((sum, luc) => {
    const biomassEmission = extractEmissionValue(luc.footprints.biomassEmissionFootprint) * hectarRatio;
    const deadOrganicMatterEmission = extractEmissionValue(luc.footprints.deadOrganicMatterFootprint) * hectarRatio;
    const socChangeEmission = extractEmissionValue(luc.footprints.socChangeFootprint) * hectarRatio;
    return sum + biomassEmission + deadOrganicMatterEmission + socChangeEmission;
  }, 0);

  /** FERTILIZERS && PESTICIDES */
  const cropOrganicFertilizerEmissions = sumNumbers([
    ...cropCalculation.calculatedFootprint.organicFertilizerFootprint.NH3VolatilizationCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.organicFertilizerFootprint.leachedNitrateCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.organicFertilizerFootprint.organicFertilizerDirectCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
  ]);

  const cropMineralFertilizerEmissions = sumNumbers([
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.NH3VolatilizationCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.leachedNitrateCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.directCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.productionCO2Eq.map(({ emissions }) => extractEmissionValue(emissions)),
    ...cropCalculation.calculatedFootprint.mineralFertilizerFootprint.totalNeutralisation.map(({ emissions }) => extractEmissionValue(emissions)),
  ]);

  const cropLimeFertilizerEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.agriculturalLimeFootprint.emissions);

  const totalFertilizerEmissions = sumNumbers([cropOrganicFertilizerEmissions, cropMineralFertilizerEmissions, cropLimeFertilizerEmissions]);

  /** PLANT PROTECTIONS */
  const cropPlantProtectionEmissions = sumNumbers([
    ...cropCalculation.calculatedFootprint.plantProtectionFootprint.map(({ emissions }) => extractEmissionValue(emissions)),
  ]);
  const totalPlantProtectionEmissions = cropPlantProtectionEmissions; // Trust the naming scheme

  /** TRANSPORTATION */
  const cropUpstreamTransportEmission = sumNumbers([
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.mineralFertilizers.map(({ footprint }) => extractEmissionValue(footprint)),
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.organicFertilizers.map(({ footprint }) => extractEmissionValue(footprint)),
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.plantProtection.map(({ footprint }) => extractEmissionValue(footprint)),
    ...cropCalculation.calculatedFootprint.upstreamTransportationFootprint.seeding.map(({ footprint }) => extractEmissionValue(footprint)),
  ]);
  const cropDownstreamTransportEmission = sumNumbers(
    cropCalculation.calculatedFootprint.downstreamTransportationFootprint.crop.map(({ footprint }) => extractEmissionValue(footprint))
  );

  const cropTotalTransportationEmissions = cropUpstreamTransportEmission + cropDownstreamTransportEmission;

  /** FIELD WORK  */
  const cropFieldWorkHarvestingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dieselFootprint.harvestDieselUsage.footprint);
  const cropFieldWorkFertilizerApplicationEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.fertilizerApplication.map(({ footprint }) => extractEmissionValue(footprint))
  );
  const cropFieldWorkPlantProtectionApplicationsEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.plantProtectionApplications.map(({ footprint }) => extractEmissionValue(footprint))
  );
  const cropFieldWorkSeedingApplicationEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.seedingApplication.map(({ footprint }) => extractEmissionValue(footprint))
  );
  const cropFieldWorkTillageApplicationEmissions = sumNumbers(
    cropCalculation.calculatedFootprint.dieselFootprint.tillageApplication.map(({ footprint }) => extractEmissionValue(footprint))
  );

  const totalFieldWorkEmissions = sumNumbers([
    cropFieldWorkHarvestingEmissions,
    cropFieldWorkFertilizerApplicationEmissions,
    cropFieldWorkPlantProtectionApplicationsEmissions,
    cropFieldWorkSeedingApplicationEmissions,
    cropFieldWorkTillageApplicationEmissions,
  ]);

  /** DRYING */
  const cropRenewableElectricityDryingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dryingFootprint?.renewableElectricity);
  const cropElectricityDryingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dryingFootprint?.electricity);
  const cropNaturalGasDryingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dryingFootprint?.naturalGas);
  const cropDieselDryingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dryingFootprint?.diesel);
  const cropHeatingOilDryingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dryingFootprint?.heatingOil);
  const cropBenzineDryingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dryingFootprint?.benzine);
  const cropWoodchipHeaterDryingEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.dryingFootprint?.woodchipHeater);

  const totalDryingEnergyEmissions = sumNumbers([
    cropRenewableElectricityDryingEmissions,
    cropElectricityDryingEmissions,
    cropNaturalGasDryingEmissions,
    cropDieselDryingEmissions,
    cropHeatingOilDryingEmissions,
    cropBenzineDryingEmissions,
    cropWoodchipHeaterDryingEmissions,
  ]);

  /** CROP RESIDUE */
  const residueDirectEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.cropResidueFootprint.directN2OCO2Eq);
  const residueLeachedNitrateEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.cropResidueFootprint.leachedNitrateCO2Eq);
  const totalResidueEmissions = sumNumbers([residueDirectEmissions, residueLeachedNitrateEmissions]);

  /** CROP Irrigation */
  const cropIrrigationEmissions = extractEmissionValue(cropCalculation.calculatedFootprint.irrigationFootprint);

  /** OTHER */
  const totalWaterEmissions = sumNumbers([farmWastewaterEmission, cropIrrigationEmissions]);
  const totalOtherEmissions = sumNumbers([farmWastewaterEmission, cropIrrigationEmissions]);

  const totalEmissions = sumNumbers([
    totalFertilizerEmissions,
    totalPlantProtectionEmissions,
    cropTotalTransportationEmissions,
    totalFieldWorkEmissions,
    totalDryingEnergyEmissions,
    totalFarmEnergyEmission,
    totalWasteEmission,
    totalRefrigerantUsageEmissions,
    totalResidueEmissions,
    totalLandUseChangeEmission,
    totalOtherEmissions,
  ]);

  const tableData: FootprintingResultsDataTable = {
    fertilizer: {
      data: createDataRow(totalFertilizerEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [
        createSubdataRow("mineralFertilizers", cropMineralFertilizerEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("organicFertilizers", cropOrganicFertilizerEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("lime", cropLimeFertilizerEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      ],
    },
    plantProtection: {
      data: createDataRow(cropPlantProtectionEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [],
    },
    transportation: {
      data: createDataRow(cropTotalTransportationEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [
        createSubdataRow("upstreamTransportation", cropUpstreamTransportEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("downstreamTransportation", cropDownstreamTransportEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
      ],
    },
    fieldWork: {
      data: createDataRow(totalFieldWorkEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [
        createSubdataRow("harvesting", cropFieldWorkHarvestingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("fertilizerApplication", cropFieldWorkFertilizerApplicationEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("plantProtectionApplications", cropFieldWorkPlantProtectionApplicationsEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("seedingApplication", cropFieldWorkSeedingApplicationEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("tillageApplication", cropFieldWorkTillageApplicationEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      ],
    },
    energy: {
      data: createDataRow(totalDryingEnergyEmissions + totalFarmEnergyEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [
        createSubdataRow("diesel", farmDieselEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("dieselDrying", cropDieselDryingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("benzin", farmBenzinEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("benzinDrying", cropBenzineDryingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("naturalGas", farmNaturalGasEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("naturalGasDrying", cropNaturalGasDryingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("heating", farmHeatingOilEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("heatingDrying", cropHeatingOilDryingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("electricity", farmElectricityEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("electricityDrying", cropElectricityDryingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("renewableElectricityDrying", cropRenewableElectricityDryingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
        createSubdataRow("woodchipHeaterDrying", cropWoodchipHeaterDryingEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      ],
    },
    waste: {
      data: createDataRow(totalWasteEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [],
    },
    refrigerants: {
      data: createDataRow(totalRefrigerantUsageEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [], // TODO
    },
    residue: {
      data: createDataRow(totalResidueEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [],
    },
    luc: {
      data: createDataRow(totalLandUseChangeEmission, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [],
    },
    other: {
      data: createDataRow(totalOtherEmissions, totalCropHectars, totalCropYieldKg, totalEmissions),
      subData: [createSubdataRow("water", totalWaterEmissions, totalCropHectars, totalCropYieldKg, totalEmissions)],
    },
  };

  return {
    tableData,
    overall: {
      totalCO2e: totalEmissions,
      perKgYieldCO2e: totalEmissions / totalCropYieldKg,
      perHaCO2e: totalEmissions / totalCropHectars,
    },
  };
}

function addFootprintSummaryMetrics(
  a: FootprintSummaryMetrics,
  b: FootprintSummaryMetrics,
  totalYieldKg: number,
  totalFieldHa: number
): FootprintSummaryMetrics {
  return {
    totalCO2e: _.round(sumFloatingPointNumbers(a.totalCO2e, b.totalCO2e), 3),
    perKgYieldCO2e: _.round(sumFloatingPointNumbers(a.totalCO2e, b.totalCO2e) / totalYieldKg, 3),
    perHaCO2e: _.round(sumFloatingPointNumbers(a.totalCO2e, b.totalCO2e) / totalFieldHa, 3),
  };
}

export function sumFootprintCalculationSummaries(
  summaries: FootprintCalculationSummary[],
  totalYieldKg: number,
  totalFieldHa: number
): FootprintCalculationSummary {
  return summaries.reduce(
    (total, fcs) => {
      const overall = addFootprintSummaryMetrics(total.overall, fcs.overall, totalYieldKg, totalFieldHa);
      return {
        overall,
        tableData: {
          fertilizer: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.fertilizer.data.totalCO2e, fcs.tableData.fertilizer.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.fertilizer.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.fertilizer.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          plantProtection: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.plantProtection.data.totalCO2e, fcs.tableData.plantProtection.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.plantProtection.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.plantProtection.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          transportation: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.transportation.data.totalCO2e, fcs.tableData.transportation.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.transportation.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.transportation.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          fieldWork: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.fieldWork.data.totalCO2e, fcs.tableData.fieldWork.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.fieldWork.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.fieldWork.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          energy: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.energy.data.totalCO2e, fcs.tableData.energy.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.energy.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.energy.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          waste: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.waste.data.totalCO2e, fcs.tableData.waste.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.waste.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.waste.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          refrigerants: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.refrigerants.data.totalCO2e, fcs.tableData.refrigerants.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.refrigerants.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.refrigerants.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          residue: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.residue.data.totalCO2e, fcs.tableData.residue.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.residue.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.residue.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          luc: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.luc.data.totalCO2e, fcs.tableData.luc.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.luc.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.luc.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
          other: {
            data: createDataRow(
              sumFloatingPointNumbers(total.tableData.other.data.totalCO2e, fcs.tableData.other.data.totalCO2e),
              totalFieldHa,
              totalYieldKg,
              overall.totalCO2e
            ),
            subData: fcs.tableData.other.subData.map((sdr): FootprintingResultsSubDataRowData => {
              const otherRow = total.tableData.other.subData.find((item) => item.source === sdr.source);
              return {
                source: sdr.source,
                ...createDataRow(sumFloatingPointNumbers(sdr.totalCO2e, otherRow?.totalCO2e ?? 0), totalFieldHa, totalYieldKg, overall.totalCO2e),
              };
            }),
          },
        },
      };
    },
    _.clone(EMPTY_SUMMARY) as FootprintCalculationSummary
  );
}

export const getMidpointGrowingDate = (practiceCrops: WithInjectedName<PracticeCrop>[] | undefined, cropId: string, year: number): Date => {
  const practiceCrop = practiceCrops?.find((crop) => crop.id === cropId);

  if (!practiceCrop?.sequestrationValues) {
    return moment().year(year).endOf("year").toDate();
  }
  const sequestrationValues = practiceCrop?.sequestrationValues?.[0];
  const regionalSeedingStartDate = moment(sequestrationValues?.seedingStart, "DD/MM").year(year);
  const regionalHarvestEndDate = moment(sequestrationValues?.harvestEnd, "DD/MM").year(year);

  const isWinterCrop = practiceCrop?.isWinterCrop;
  if (isWinterCrop) {
    regionalHarvestEndDate.add(1, "year");
  }

  return regionalSeedingStartDate
    .clone()
    .add(regionalHarvestEndDate.diff(regionalSeedingStartDate) / 2)
    .toDate();
};
